import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';

import { MatSelectInfiniteScrollDirective } from '../../directives/mat-select-infinite-scroll/mat-select-infinite-scroll.directive';
import { SearchBarComponentModule } from '../search-bar/search-bar.component';

/**
 * Interface for items that can be used in the searchable single selector.
 * Items must have at least a `name` property for display.
 */
export interface SearchableSingleSelectorItem {
  id: string | number;
  name: string;

  /**
   * Tag to display on the item.
   * If not provided, no tag will be displayed.
   */
  tag?: {
    label: string;
    type: 'info' | 'warning' | 'error';
  };
}

@Component({
    selector: 'ts-searchable-single-selector',
    templateUrl: './searchable-single-selector.component.html',
    styleUrls: ['./searchable-single-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TranslateModule,
        MatFormFieldModule,
        MatSelectModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        SearchBarComponentModule,
        MatSelectInfiniteScrollDirective,
    ]
})
export class SearchableSingleSelectorComponent<T extends SearchableSingleSelectorItem> {
  @Input() loaded: boolean;
  @Input() options: T[];
  @Input() selected: T | null = null;
  @Input() searchTerm: string;

  /** Accessible label for the selector */
  @Input() ariaLabel = '';

  /** Data attribute for testing purposes */
  @Input() dataCy = 'searchable-single-selector';

  /** Hint text shown when no item is selected */
  @Input() hint = '';

  /**
   * i18n key for an error message rendered below the field.
   * When truthy, the field shell is also painted with the error border.
   */
  @Input() errorKey: string | null = null;

  /** Whether to show the search bar. Defaults to true */
  @Input() searchable = true;

  /** Custom message shown when no items are available. Defaults to 'info.no_items' translation key */
  @Input() noItemsMessage: string;

  /** Property name to use for display text. Defaults to 'name' */
  @Input() displayProperty: keyof T | 'name' = 'name';

  @Output() searchTermChanged = new EventEmitter<string>();
  @Output() lastItemReached = new EventEmitter<void>();
  @Output() selectedChange = new EventEmitter<T>();

  public get allOptions(): T[] {
    return this.options || [];
  }

  public get displayName(): string {
    if (!this.selected) {
      return this.hint;
    }
    return this.getDisplayValue(this.selected);
  }

  /** Get the display value for an item using the displayProperty */
  public getDisplayValue(item: T): string {
    return String(item[this.displayProperty as keyof T] ?? item.name);
  }

  public selectionChanged(item: T) {
    this.selected = item;
    this.selectedChange.emit(item);
  }

  public checkIfItemIsSelected(item1?: T, item2?: T) {
    return item1?.id === item2?.id;
  }
}
